import { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { PhoneInput } from "@/components/ui/phone-input"
import { SSNInput } from "@/components/ui/ssn-input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AddressInputs } from '@/components/address-inputs'
import { usePrequalPasteHelper } from '@/hooks/use-prequal-paste-helper'
import { extractDate, toPrequalSubmitBirthDate, yyyymmdd } from '@prescreen/lib/date-utils'
import { showPrimaryPrequalForm } from '@prescreen/lib/prequal-borrower-view'
import { dollars, dollarsToNum } from '@prescreen/lib/formats'
import { apiFetch } from '@prescreen/lib/api-client'
import {
  BorrowerBrandingFrame,
  borrowerPageShellClass,
  borrowerSpinnerClass
} from '@/components/borrower/BorrowerBrandingFrame'
import { CoBorrowerFields, emptyCoBorrower } from '@/components/borrower/CoBorrowerFields'
import { CoBorrowerPrompt } from '@/components/borrower/CoBorrowerPrompt'
import { AwaitingCoBorrower } from '@/components/borrower/AwaitingCoBorrower'
import type { BorrowerPrescreenDto, CoBorrowerFormValues } from '@/types/borrower-prescreen-dto'

export default function PrequalificationPage() {
  const { requestId } = useParams<{ requestId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [prescreen, setPrescreen] = useState<BorrowerPrescreenDto | null>(null)
  const [ssn, setSsn] = useState('')
  const [includeCoBorrower, setIncludeCoBorrower] = useState(false)
  const [coBorrower, setCoBorrower] = useState<CoBorrowerFormValues>(emptyCoBorrower)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [needsSsnRetry, setNeedsSsnRetry] = useState(false)

  const applyPrescreen = useCallback((next: BorrowerPrescreenDto) => {
    setPrescreen(next)
    setNeedsSsnRetry(next.needsSsn)
  }, [])

  const loadPrescreenRequest = useCallback(async () => {
    if (!requestId || !token) {
      setError('Missing required parameters')
      setIsLoadingData(false)
      return
    }

    try {
      const response = await apiFetch(`/api/prequalification/${requestId}?token=${token}`)
      const json = await response.json()

      if (json.success) {
        const loaded = json.data.prescreenRequest as BorrowerPrescreenDto
        applyPrescreen(loaded)

        if (loaded.status === 'completed') {
          navigate(`/prequalification/${requestId}/complete?token=${token}`, { replace: true })
          return
        }

        setIsLoadingData(false)
      } else {
        setError(json.error || 'Failed to load prequalification request')
        setIsLoadingData(false)
      }
    } catch {
      setError('Network error. Please try again.')
      setIsLoadingData(false)
    }
  }, [applyPrescreen, navigate, requestId, token])

  useEffect(() => {
    void loadPrescreenRequest()
  }, [loadPrescreenRequest])

  usePrequalPasteHelper(data => {
    setPrescreen(prev =>
      prev
        ? {
            ...prev,
            ...data,
            hasSsnOnFile: prev.hasSsnOnFile,
            needsSsn: prev.needsSsn,
            coBorrowerAllowed: prev.coBorrowerAllowed
          }
        : (data as BorrowerPrescreenDto)
    )
    if (data.ssn) {
      setSsn(String(data.ssn))
    }
  })

  const handleInputChange = (field: string, value: string | boolean | number | null) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setPrescreen(prev => {
        if (!prev) return prev
        return {
          ...prev,
          [parent]: {
            ...(prev[parent as keyof BorrowerPrescreenDto] as object),
            [child]: value
          }
        } as BorrowerPrescreenDto
      })
    } else {
      setPrescreen(prev => (prev ? { ...prev, [field]: value } : prev))
    }
  }

  const buildSubmitFormData = () => {
    if (!prescreen) {
      return null
    }
    return {
      firstName: prescreen.firstName,
      middleName: prescreen.middleName,
      lastName: prescreen.lastName,
      suffix: prescreen.suffix,
      phoneNumber: prescreen.phoneNumber,
      streetAddress: prescreen.streetAddress,
      birthDate:
        typeof prescreen.birthDate === 'string'
          ? toPrequalSubmitBirthDate(prescreen.birthDate)
          : prescreen.birthDate,
      statedGrossAnnualIncome: dollarsToNum(prescreen.statedGrossAnnualIncome),
      consent: prescreen.consent,
      ssn: ssn || undefined
    }
  }

  const handleSubmit = async (e: React.FormEvent, options?: { continueAlone?: boolean }) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    setStatusMessage(null)

    const formData = buildSubmitFormData()
    if (!formData) {
      setIsLoading(false)
      return
    }

    try {
      const response = await apiFetch('/api/prequalification/submit', {
        method: 'POST',
        body: JSON.stringify({
          requestId,
          token,
          formData,
          continueAlone: options?.continueAlone === true,
          coBorrower:
            !options?.continueAlone && includeCoBorrower && prescreen?.status !== 'awaiting_coborrower'
              ? coBorrower
              : undefined
        }),
      })

      const data = await response.json()

      if (data.success) {
        if (data.data?.needsSsn) {
          setNeedsSsnRetry(true)
          setError('Social Security Number is required to complete your credit check.')
          return
        }
        if (data.data?.status === 'awaiting_coborrower') {
          if (data.data.consentEmailSent === false) {
            setStatusMessage(
              'We could not send the consent email. You can resend it from this page.'
            )
          }
          await loadPrescreenRequest()
          return
        }
        if (data.data?.redirectUrl) {
          window.location.href = data.data.redirectUrl
        } else {
          window.location.href = `/prequalification/${requestId}/complete?token=${token}`
        }
      } else {
        if (response.status === 409 && data.data?.redirectUrl) {
          window.location.href = data.data.redirectUrl
        } else {
          setError(data.error || 'Failed to submit prequalification form')
        }
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddCoBorrower = async (values: CoBorrowerFormValues) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiFetch('/api/prequalification/coborrower', {
        method: 'POST',
        body: JSON.stringify({ requestId, token, coBorrower: values })
      })
      const json = await response.json()
      if (!json.success) {
        setError(json.error || 'Failed to add co-borrower')
        return
      }
      if (json.data?.consentEmailSent === false) {
        setStatusMessage('Co-borrower saved, but the consent email could not be sent. Try resend.')
      }
      await loadPrescreenRequest()
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleContinueAlone = async () => {
    const syntheticEvent = { preventDefault: () => undefined } as React.FormEvent
    await handleSubmit(syntheticEvent, { continueAlone: true })
  }

  const handleResendConsent = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiFetch('/api/prequalification/coborrower/resend', {
        method: 'POST',
        body: JSON.stringify({ requestId, token })
      })
      const json = await response.json()
      if (!json.success) {
        setError(json.error || 'Failed to resend invitation')
        return
      }
      setStatusMessage('Consent invitation resent.')
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveCoBorrower = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiFetch('/api/prequalification/coborrower/remove', {
        method: 'POST',
        body: JSON.stringify({ requestId, token })
      })
      const json = await response.json()
      if (!json.success) {
        setError(json.error || 'Failed to remove co-borrower')
        return
      }
      await loadPrescreenRequest()
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${borrowerSpinnerClass(false)} mx-auto mb-4`}></div>
            <p className="text-gray-600">Loading prequalification request...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error && !prescreen) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Error Loading Prequalification Request</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="mt-4">
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!prescreen) return null

  const branding = prescreen.branding
  const isBranded = Boolean(branding)
  const ssnRequired = needsSsnRetry || prescreen.needsSsn
  const showPrimaryForm = showPrimaryPrequalForm(prescreen)
  const showCoBorrowerPrompt =
    prescreen.status === 'awaiting_coborrower' &&
    !prescreen.coBorrower &&
    !prescreen.coBorrowerPromptDeclinedAt
  const showAwaitingCoBorrower =
    prescreen.status === 'awaiting_coborrower' && Boolean(prescreen.coBorrower)

  return (
    <BorrowerBrandingFrame branding={branding} className={borrowerPageShellClass(isBranded)}>
      <div className="max-w-2xl mx-auto px-4">
        {!isBranded && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Complete Your Credit Prequalification
            </h1>
            <p className="text-gray-600">
              Please provide the following information to complete your prequalification process
            </p>
          </div>
        )}
        {isBranded && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Complete Your Credit Prequalification
            </h1>
            <p className="text-muted-foreground">
              Please provide the following information to complete your prequalification with{' '}
              {branding.displayName}
            </p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Credit Prequalification Authorization</CardTitle>
            <CardDescription>
              This is a soft credit check that will not affect your credit score.
              Your information is secure and encrypted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showCoBorrowerPrompt && (
              <CoBorrowerPrompt
                onAddCoBorrower={handleAddCoBorrower}
                onContinueAlone={handleContinueAlone}
                busy={isLoading}
                error={error}
              />
            )}

            {showAwaitingCoBorrower && (
              <AwaitingCoBorrower
                prescreen={prescreen}
                onResend={handleResendConsent}
                onRemove={handleRemoveCoBorrower}
                busy={isLoading}
                message={statusMessage}
                error={error}
              />
            )}

            {showPrimaryForm && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {statusMessage && (
                <Alert>
                  <AlertDescription>{statusMessage}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-4">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={prescreen.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="middleName">Middle</Label>
                    <Input
                      id="middleName"
                      value={prescreen.middleName || ''}
                      onChange={(e) => handleInputChange('middleName', e.target.value)}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={prescreen.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="suffix">Suffix</Label>
                    <Input
                      id="suffix"
                      value={prescreen.suffix || ''}
                      onChange={(e) => handleInputChange('suffix', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <PhoneInput
                    id="phoneNumber"
                    value={prescreen.phoneNumber || ''}
                    onChange={(value) => handleInputChange('phoneNumber', value)}
                    required
                  />
                </div>

                <div>
                  <Label>Email Address *</Label>
                  <Input
                    value={prescreen.userEmail || ''}
                    disabled={true}
                    required
                  />
                </div>

                <Label>Home Address</Label>
                <AddressInputs
                  address={prescreen.streetAddress}
                  onChange={streetAddress => setPrescreen({ ...prescreen, streetAddress })}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input
                      type="date"
                      value={prescreen.birthDate ? yyyymmdd(extractDate(prescreen.birthDate)) : ''}
                      onChange={(e) => handleInputChange('birthDate', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="ssn">
                      Social Security Number{ssnRequired ? ' *' : ''}
                    </Label>
                    {prescreen.hasSsnOnFile && !ssn && prescreen.ssnLast4 ? (
                      <p className="text-sm text-muted-foreground mb-2">
                        On file ending in {prescreen.ssnLast4}
                      </p>
                    ) : null}
                    <SSNInput
                      id="ssn"
                      value={ssn}
                      onChange={setSsn}
                      required={ssnRequired}
                    />
                  </div>
                </div>

                <div>
                  <Label>Individual Annual Income (before taxes) *</Label>
                  <Input
                    value={prescreen.statedGrossAnnualIncome || ''}
                    onChange={(e) => {
                      const val = e.target.value?.trim().replace(/[^$0-9.,]/g, '')
                      handleInputChange('statedGrossAnnualIncome', val)
                    }}
                    onBlur={() => {
                      handleInputChange(
                        'statedGrossAnnualIncome',
                        dollars(prescreen.statedGrossAnnualIncome)
                      )
                    }}
                    placeholder="$0.00"
                    required
                  />
                </div>

                {prescreen.coBorrowerAllowed && (
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="includeCoBorrower"
                        checked={includeCoBorrower}
                        onCheckedChange={(checked) => setIncludeCoBorrower(checked === true)}
                      />
                      <Label htmlFor="includeCoBorrower" className="text-sm font-medium">
                        Include a co-borrower on this application
                      </Label>
                    </div>
                    {includeCoBorrower && (
                      <CoBorrowerFields value={coBorrower} onChange={setCoBorrower} disabled={isLoading} />
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="consent"
                      checked={prescreen.consent ?? false}
                      onCheckedChange={(checked) => handleInputChange('consent', checked as boolean)}
                      required
                    />
                    <div className="space-y-2">
                      <Label htmlFor="consent" className="text-sm font-medium">
                        I consent to the credit prequalification process *
                      </Label>
                      <p className="text-xs text-gray-600">
                        By checking this box, I authorize the soft credit check and confirm that:
                      </p>
                      <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                        <li>I am providing accurate information</li>
                        <li>I understand this is a soft credit check that won't affect my credit score</li>
                        <li>I consent to the processing of my personal information for this prequalification</li>
                        <li>I understand my information will be securely transmitted and stored</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? 'Processing...' : 'Submit Prequalification Request'}
                </Button>
              </div>
            </form>
            )}
          </CardContent>
        </Card>

        <div className={`mt-6 text-center text-sm ${isBranded ? 'text-muted-foreground' : 'text-gray-500'}`}>
          <p>Your information is encrypted and secure. This process typically takes 2-3 minutes.</p>
          {requestId && (
            <p className="mt-2 text-xs">Request ID: {requestId}</p>
          )}
        </div>
      </div>
    </BorrowerBrandingFrame>
  )
}
